import { useRef, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  CalendarCheck,
  CalendarPlus,
  Calendar,
  X,
} from 'lucide-react';
import type { SortedVisitItem } from './hooks/useSortedVisits';

interface MenuAction {
  label: string;
  icon: React.ReactNode;
  className: string;
  onClick: () => void;
}

interface VisitContextMenuProps {
  item: SortedVisitItem;
  position: { x: number; y: number };
  onClose: () => void;
  onSetUrgent: (visitTargetId: number) => void;
  onRemoveUrgent: (visitTargetId: number) => void;
  onPlanToday: (visitTargetId: number) => void;
  onCancelVisit: (visitTargetId: number) => void;
  onCreateVisit: (visitTargetId: number) => void;
  onScheduleDate: () => void;
  onMarkSkipped: (visitTargetId: number) => void;
}

export function VisitContextMenu({
  item,
  position,
  onClose,
  onSetUrgent,
  onRemoveUrgent,
  onPlanToday,
  onCancelVisit,
  onCreateVisit,
  onScheduleDate,
  onMarkSkipped,
}: VisitContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState(position);

  const { visit } = item;
  const visitTargetId = item.visitTargetId;

  // Derived state
  const todayStr = new Date().toISOString().split('T')[0];
  const isVisitActive = !visit?.plannedDate || visit.plannedDate <= todayStr;
  const isUrgent = visit?.priority === 'URGENT' && isVisitActive;
  const hasPlannedToday =
    visit?.plannedDate &&
    new Date(visit.plannedDate).toDateString() === new Date().toDateString();
  const hasPendingVisit = visit?.status === 'PENDING' && isVisitActive;
  const isCompleted = visit?.status === 'VISITED' || visit?.status === 'SKIPPED';

  // Build actions based on state
  const actions: MenuAction[] = [];

  // For completed visits — show regular (State 4) buttons
  if (isCompleted || (!isUrgent && !hasPlannedToday && !hasPendingVisit)) {
    // State 4: Regular
    actions.push({
      label: 'Срочный',
      icon: <AlertTriangle className="h-4 w-4" />,
      className: 'border-amber-300 bg-amber-50 text-amber-900',
      onClick: () => { onSetUrgent(visitTargetId); onClose(); },
    });
    actions.push({
      label: 'Заехать сегодня',
      icon: <CalendarCheck className="h-4 w-4" />,
      className: 'border-orange-300 bg-orange-50 text-orange-800',
      onClick: () => { onPlanToday(visitTargetId); onClose(); },
    });
    actions.push({
      label: 'Запланировать визит',
      icon: <CalendarPlus className="h-4 w-4" />,
      className: 'border-green-300 bg-green-50 text-green-800',
      onClick: () => { onCreateVisit(visitTargetId); onClose(); },
    });
    actions.push({
      label: 'Запланировать дату',
      icon: <Calendar className="h-4 w-4" />,
      className: 'border-blue-300 bg-blue-50 text-blue-800',
      onClick: () => { onScheduleDate(); onClose(); },
    });
    if (!isCompleted) {
      actions.push({
        label: 'Пропуск визита',
        icon: <X className="h-4 w-4" />,
        className: 'border-gray-300 bg-gray-50 text-gray-700',
        onClick: () => { onMarkSkipped(visitTargetId); onClose(); },
      });
    }
  } else if (isUrgent) {
    // State 1: Already urgent
    actions.push({
      label: 'Снять срочность',
      icon: <AlertTriangle className="h-4 w-4" />,
      className: 'border-amber-400 bg-amber-100 text-amber-900',
      onClick: () => { onRemoveUrgent(visitTargetId); onClose(); },
    });
    actions.push({
      label: 'Запланировать дату',
      icon: <Calendar className="h-4 w-4" />,
      className: 'border-blue-300 bg-blue-50 text-blue-800',
      onClick: () => { onScheduleDate(); onClose(); },
    });
    actions.push({
      label: 'Пропуск визита',
      icon: <X className="h-4 w-4" />,
      className: 'border-gray-300 bg-gray-50 text-gray-700',
      onClick: () => { onMarkSkipped(visitTargetId); onClose(); },
    });
  } else if (hasPlannedToday) {
    // State 2: Planned for today
    actions.push({
      label: 'Срочный',
      icon: <AlertTriangle className="h-4 w-4" />,
      className: 'border-amber-300 bg-amber-50 text-amber-900',
      onClick: () => { onSetUrgent(visitTargetId); onClose(); },
    });
    actions.push({
      label: 'Запланировать дату',
      icon: <Calendar className="h-4 w-4" />,
      className: 'border-blue-300 bg-blue-50 text-blue-800',
      onClick: () => { onScheduleDate(); onClose(); },
    });
    actions.push({
      label: 'Отменить заехать сегодня',
      icon: <X className="h-4 w-4" />,
      className: 'border-gray-300 bg-gray-50 text-gray-700',
      onClick: () => { onCancelVisit(visitTargetId); onClose(); },
    });
    actions.push({
      label: 'Пропуск визита',
      icon: <X className="h-4 w-4" />,
      className: 'border-gray-300 bg-gray-50 text-gray-700',
      onClick: () => { onMarkSkipped(visitTargetId); onClose(); },
    });
  } else if (hasPendingVisit) {
    // State 3: Pending visit
    actions.push({
      label: 'Срочный',
      icon: <AlertTriangle className="h-4 w-4" />,
      className: 'border-amber-300 bg-amber-50 text-amber-900',
      onClick: () => { onSetUrgent(visitTargetId); onClose(); },
    });
    actions.push({
      label: 'Заехать сегодня',
      icon: <CalendarCheck className="h-4 w-4" />,
      className: 'border-orange-300 bg-orange-50 text-orange-800',
      onClick: () => { onPlanToday(visitTargetId); onClose(); },
    });
    actions.push({
      label: 'Запланировать дату',
      icon: <Calendar className="h-4 w-4" />,
      className: 'border-blue-300 bg-blue-50 text-blue-800',
      onClick: () => { onScheduleDate(); onClose(); },
    });
    actions.push({
      label: 'Отменить запланированное',
      icon: <X className="h-4 w-4" />,
      className: 'border-gray-300 bg-gray-50 text-gray-700',
      onClick: () => { onCancelVisit(visitTargetId); onClose(); },
    });
    actions.push({
      label: 'Пропуск визита',
      icon: <X className="h-4 w-4" />,
      className: 'border-gray-300 bg-gray-50 text-gray-700',
      onClick: () => { onMarkSkipped(visitTargetId); onClose(); },
    });
  }

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;

    const rect = menu.getBoundingClientRect();
    const pad = 8;
    let { x, y } = position;

    if (x + rect.width > window.innerWidth - pad) {
      x = window.innerWidth - rect.width - pad;
    }
    if (x < pad) x = pad;

    if (y + rect.height > window.innerHeight - pad) {
      y = window.innerHeight - rect.height - pad;
    }
    if (y < pad) y = pad;

    setAdjustedPos({ x, y });
  }, [position]);

  return createPortal(
    <div
      className="fixed inset-0 z-50"
      onClick={onClose}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        ref={menuRef}
        className="fixed bg-white dark:bg-gray-900 rounded-lg shadow-xl border p-1.5 min-w-[200px] space-y-1"
        style={{ left: adjustedPos.x, top: adjustedPos.y }}
        onClick={(e) => e.stopPropagation()}
      >
        {actions.map((action) => (
          <button
            key={action.label}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium border transition-colors active:scale-[0.97] ${action.className}`}
            onClick={action.onClick}
          >
            {action.icon}
            {action.label}
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
}
